import { CustomDialog } from '../../../../components/CustomDialog';
import { useState } from 'react';
import {
  AutoCompleteController,
  Button,
  InputController,
} from '../../../../components';
import { useAppDispatch } from '../../../../storeHook';
import { showAlert } from '../../../../services/alert';
import {
  useCancelOrderMutation,
  useCancelOrderV2Mutation,
  useOrderActionDocumentUploadMutation,
} from '../../../../services/orders';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import { UploadFileController } from '../../../../components/formControllers/uploadFile';
import { TButtonVariant } from '../../../../types';
import { INITIATED_BY_OPTIONS } from '../orderItems/hepler';
import {
  TOrder,
  TRetailerSalesOrderLineItem,
} from '../../../../utils/types/orders';
import './index.scss';

const RETURN_REASON_OPTIONS = [
  {
    label: 'Product damaged externally',
    value: 'Product damaged externally',
  },
  {
    label: 'Product damaged internally',
    value: 'Product damaged internally',
  },
  {
    label: 'Product expired',
    value: 'Product expired',
  },
  {
    label: 'Product near expiry',
    value: 'Product near expiry',
  },
  {
    label: 'Missing Item (external and internal)',
    value: 'Missing Item (external and internal)',
  },
  {
    label: 'Found better price outside',
    value: 'Found better price outside',
  },
  {
    label: 'Different product received',
    value: 'Different product received',
  },
  {
    label: 'Fake product',
    value: 'Fake product',
  },
  {
    label: 'No longer needed',
    value: 'No longer needed',
  },
  {
    label: 'Retailer not accepting',
    value: 'Retailer not accepting',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

const formSchema = yup.object({
  reason: yup.string().required('please select reason'),
  otherReason: yup
    .string()
    .when('reason', ([reason], schema) => {
      return reason == 'Other'
        ? schema.required('please enter reason here')
        : schema;
    }),
  initiator: yup
    .string()
    .required('please select initiator'),
  uploadFile: yup
    .mixed()
    .required('please upload invoice file')
    .test(
      'fileSize',
      'Please attach image size upto 500KB',
      (file) => {
        if (!file) return true;
        return (file as File)?.size <= 500 * 1000;
      }
    ),
});
type TFormData = yup.InferType<typeof formSchema>;

export const ReturnOrderBtnAndPopUp: React.FC<{
  orderDetails: TOrder;
  lineItemDetails?: TRetailerSalesOrderLineItem;
}> = ({ orderDetails, lineItemDetails }) => {
  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    watch,
    register,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
  });

  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [orderAction] = orderDetails.partCancelReturnAllowed
    ? useCancelOrderMutation()
    : useCancelOrderV2Mutation();
  const [uploadMedia] =
    useOrderActionDocumentUploadMutation();

  let type = 'ORDER_FULL';
  let noteText =
    'Note : On click of ‘Return order’, return will get initiated for thus product.';
  let btnText = 'Return Entire Order';
  let buttonVeriant: TButtonVariant = 'button--secondary';

  if (lineItemDetails) {
    type = 'LINE_ITEM_FULL';
    noteText =
      'Note : On click of ‘Return order’, return will get initiated for this product. Retailer will be refunded and the amount will be reversed from seller in case already paid to him. ';
    btnText = 'Return Full';
    buttonVeriant = 'button--quaternary';
  }

  const uploadMediaAction = (formData: TFormData) => {
    const data = new FormData();
    data.append('file', formData.uploadFile as File);
    data.append('type', type);
    data.append('orderId', `${orderDetails.id}`);
    if (lineItemDetails) {
      data.append('lineItemId', `${lineItemDetails.id}`);
    }

    uploadMedia(data)
      .unwrap()
      .then(() => {
        dispatch(
          showAlert({
            content: 'Media Uploaded Successfully',
          })
        );
      })
      .catch((err) => {
        console.log('error', err);
        // this.utility.handleError(err);
      });
  };

  const onSubmit = (formData: TFormData) => {
    setOpen(false);
    uploadMediaAction(formData);
    orderAction({
      reason:
        formData.reason === 'Other'
          ? formData.otherReason || ''
          : formData.reason,
      initiator: formData.initiator,
      media: formData.uploadFile as File,
      type,
      retailerSalesOrderId: orderDetails.id,
      ...(lineItemDetails
        ? {
            lineItemId: lineItemDetails.id,
            qty: lineItemDetails.originalTargetQuantity,
          }
        : {}),
    })
      .unwrap()
      .then((res) => {
        dispatch(
          showAlert({
            content: res.message,
          })
        );
      })
      .catch((err) => {
        console.log('error', err);
        // this.utility.handleError(err);
      });
  };

  const product = lineItemDetails?.product;
  const popUpTitle =
    (product?.productName
      ? `${product?.productName} - ${product.unitMeins}`
      : product?.maktx) || 'Return Entire Order';

  return (
    <>
      <Button
        onClick={() => setOpen(!open)}
        className="ml-2"
        variant={buttonVeriant}>
        {btnText}
      </Button>
      <CustomDialog
        isForm
        dialogTitle={popUpTitle ? popUpTitle : btnText}
        open={open}
        setOpen={setOpen}
        onSubmit={handleSubmit(onSubmit)}
        actionText={{
          submit: 'Return Order',
          cancel: 'Cancel',
        }}
        maxWidth="sm"
        noteEle={
          <div className="note-container py-2 px-3">
            {noteText}
          </div>
        }>
        <div className="my-10 grid grid-cols-2 gap-10">
          <AutoCompleteController
            label="Return Reason"
            control={control}
            name="reason"
            options={RETURN_REASON_OPTIONS}
            error={errors.reason?.message}
            required
          />
          <AutoCompleteController
            label="Initiated by"
            control={control}
            name="initiator"
            options={INITIATED_BY_OPTIONS}
            error={errors.initiator?.message}
            required
          />
          {watch('reason') === 'Other' && (
            <InputController
              label="Enter reason"
              register={register('otherReason')}
              error={errors.otherReason?.message}
              required
            />
          )}
          <div className="col-span-full">
            <UploadFileController
              label="Upload file"
              allowedMediaTypes="image/png, image/jpeg, .pdf"
              handleFile={(file) =>
                setValue('uploadFile', file)
              }
              required
              error={errors?.uploadFile?.message}
            />
            <div className="td-value--light -mt-6">
              {`328 x 140 | png, jpeg, PDF | < 500kb`}
            </div>
          </div>
        </div>
      </CustomDialog>
    </>
  );
};
